import React from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import V3OfferLineupCard from './V3OfferLineupCard';
import { splitHeadliner, reorderSupports } from '../utils/lineupOrdering';
import { poolMemberKey } from '../utils/buildPoolEngineInput';

// Cards + drag-and-drop + "+ Add artist" button. The desktop rail wraps
// this in a 280px aside with a header and footer; mobile renders this
// directly inside the Lineup tab.
const V3OfferLineupList = ({
  lineup,
  activeOfferId,
  onSelectOffer,
  onAddArtist,
  onReorder,
  poolActive,
  takeByPerformerId,
}) => {
  const [headliner, supports] = splitHeadliner(lineup);

  // O-2783: in Pool mode show the member's computed pool take on each card
  // (its individual guarantee is 0). `null` outside pool mode → card falls back
  // to dollar_amount, preserving Independent behavior.
  const poolTakeFor = (cardOffer) => (
    poolActive ? (takeByPerformerId.get(poolMemberKey(cardOffer)) ?? 0) : null
  );

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    const nextSupports = reorderSupports(supports, result.source.index, result.destination.index);
    onReorder(nextSupports.map((s) => s.id));
  };

  return (
    <>
      <div className="lineup-rail__list">
        {headliner && (
          <V3OfferLineupCard
            offer={headliner}
            isActive={headliner.id === activeOfferId}
            onSelect={onSelectOffer}
            poolTake={poolTakeFor(headliner)}
          />
        )}

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="lineup-supports">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {supports.map((offer, idx) => (
                  <Draggable key={offer.id} draggableId={String(offer.id)} index={idx}>
                    {(dragProvided) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                      >
                        <V3OfferLineupCard
                          offer={offer}
                          isActive={offer.id === activeOfferId}
                          onSelect={onSelectOffer}
                          poolTake={poolTakeFor(offer)}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      <button
        type="button"
        className="lineup-rail__add"
        onClick={onAddArtist}
      >
        + Add artist to lineup
      </button>
    </>
  );
};

V3OfferLineupList.propTypes = {
  lineup: PropTypes.arrayOf(PropTypes.object).isRequired,
  activeOfferId: PropTypes.number,
  onSelectOffer: PropTypes.func.isRequired,
  onAddArtist: PropTypes.func.isRequired,
  onReorder: PropTypes.func.isRequired,
  poolActive: PropTypes.bool,
  takeByPerformerId: PropTypes.instanceOf(Map),
};

V3OfferLineupList.defaultProps = {
  activeOfferId: null,
  poolActive: false,
  takeByPerformerId: new Map(),
};

export default V3OfferLineupList;
